/**
 * Customer 360 › At a glance, the two summary cards the owner chose from the 20 ideas (2026-10-19, ideas 4 and 10):
 *   · "Why Prime / Healthy / Watch / At risk" — the reasons in at most three lines: Prime's own facts (api.prime_customers,
 *     137000: months with us, bills paid on time, no pause), else the nightly health's reasons (api.customer_health);
 *   · "Days of bill owed" — what is owed as days of the customer's average daily bill, on a meter marked with their tier's
 *     three limits L1 / L2 / L3 (api.payment_reminders, 128710: the same figure the Money page's reminders use).
 * Both are reads the desk already makes (the same query keys); nothing is computed on the server for them.
 */
import { useQuery } from '@tanstack/react-query';
import { rpc } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Panel, PanelHeader, cx } from '../../ui/primitives';
import { useOpenTab } from './CustomerViews';
import { inr, shortDay } from './customerReads';

type Level = 'GREEN' | 'AMBER' | 'RED';
interface PrimeRow { customer_id: string; since: string | null; months: number; bills: number; bills_on_time: number }
interface Owing {
  id: string; owedPaise: number; dueNowPaise: number; daysOfBill: number | null; limit: 'L1' | 'L2' | 'L3' | null; limits: [number, number, number] | null;
  tier: 'NEW' | 'STANDARD' | 'TRUSTED'; avgDailyPaise: number | null; pastDueDays: number; dueOn: string | null; lastPaidOn: string | null;
}

const WORD: Record<Level, { word: string; dot: string }> = {
  GREEN: { word: 'Healthy', dot: 'bg-accepted' },
  AMBER: { word: 'Watch', dot: 'bg-warn-mark' },
  RED: { word: 'At risk', dot: 'bg-crit-mark' },
};
const TIER: Record<Owing['tier'], string> = { NEW: 'new', STANDARD: 'standard', TRUSTED: 'trusted' };

export function usePrimeRows() {
  return useQuery({ queryKey: ['prime-customers-rows'], staleTime: 300_000, retry: false, queryFn: () => rpc<PrimeRow[]>('prime_customers') });
}

export function CustomerInsightCards({ customerId }: { customerId: string }) {
  const { can } = useSession();
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <WhyCard customerId={customerId} />
      {can('customer.payment.view') ? <DuesMeter customerId={customerId} /> : null}
    </div>
  );
}

function WhyCard({ customerId }: { customerId: string }) {
  const prime = usePrimeRows();
  const health = useQuery({ queryKey: ['customer-health', customerId], queryFn: () => rpc<{ level: Level; reasons: { reason: string; text: string }[] }>('customer_health', { p_customer_id: customerId }) });
  const p = prime.data?.find((r) => r.customer_id === customerId);
  const h = health.data;
  const head = p ? { word: 'Prime', dot: 'bg-prime' } : h ? WORD[h.level] : null;
  const lines: string[] = p
    ? [`With us ${p.months} months${p.since ? `, since ${shortDay(p.since)}` : ''}`,
       `Paid ${p.bills_on_time} of the last ${p.bills} bills within 25 days`,
       'No pause in the last 90 days']
    : h ? (h.reasons.length ? h.reasons.slice(0, 3).map((r) => r.text) : ['No missed day, complaint or pause lately', 'Paying within 7 delivery days', 'Not yet Prime: needs 6 months with us and bills paid on time'])
    : [];
  const more = !p && h ? h.reasons.length - 3 : 0;
  return (
    <Panel>
      <PanelHeader title={head ? <span className="inline-flex items-center gap-2"><span aria-hidden className={cx('size-2.5 rounded-full', head.dot)} />Why {head.word}</span> : 'Why this colour'} />
      {lines.length === 0 ? <p className="px-4 pb-4 text-small text-ink-muted">{health.isLoading ? 'Loading…' : 'Health is shown to whoever may see customers.'}</p> : (
        <ul className="grid gap-1.5 px-4 pb-4 text-body">
          {lines.map((t) => <li key={t} className="flex gap-2"><span aria-hidden className="text-ink-muted">•</span><span className="min-w-0">{t}</span></li>)}
          {more > 0 ? <li className="text-small text-ink-muted">and {more} more</li> : null}
        </ul>)}
    </Panel>
  );
}

function DuesMeter({ customerId }: { customerId: string }) {
  const openTab = useOpenTab();
  const q = useQuery({ queryKey: ['payment-reminders', 'everyone'], staleTime: 300_000,
    queryFn: () => rpc<{ customers: Owing[] }>('payment_reminders', { p_day: null, p_everyone: true }) });
  const o = q.data?.customers.find((c) => c.id === customerId);
  const limits = o?.limits ?? null;
  const days = o?.daysOfBill ?? 0;
  const scale = limits ? Math.max(limits[2] * 1.2, days) : 60;
  const pct = (n: number) => `${Math.min(100, (n / scale) * 100)}%`;
  const tone = o?.limit === 'L3' ? 'text-refused' : o?.limit === 'L2' || o?.limit === 'L1' ? 'text-queued' : 'text-ink';
  return (
    <Panel>
      <PanelHeader title="Days of bill owed" note={o ? `${TIER[o.tier]} customer · limits ${limits?.join(' / ') ?? '—'} days` : undefined}
        actions={<button type="button" className="text-small text-brand hover:underline" onClick={() => openTab('money', 'dues')}>details</button>} />
      {q.isLoading ? <p className="px-4 pb-4 text-small text-ink-muted">Loading…</p>
        : q.error ? <p className="px-4 pb-4 text-small text-ink-muted">Dues are shown to whoever may see payments.</p>
        : !o ? <p className="px-4 pb-4 text-body text-accepted">Nothing owed.</p> : (
        <div className="grid gap-2 px-4 pb-4">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className={cx('text-[1.75rem] font-semibold leading-none tabular-nums', tone)}>{o.daysOfBill === null ? '—' : `${o.daysOfBill} days`}</span>
            <span className="text-body text-ink-muted">{inr(o.owedPaise)} owed{o.avgDailyPaise ? ` · about ${inr(o.avgDailyPaise)} a day` : ''}</span>
          </div>
          {limits ? (
            <div className="pt-1" role="img" aria-label={`${days} days owed; limits ${limits.join(', ')} days`}>
              <div className="relative h-2.5 overflow-hidden rounded-full bg-accepted">
                <span className="absolute inset-y-0 bg-warn-mark" style={{ left: pct(limits[0]), right: `calc(100% - ${pct(limits[2])})` }} />
                <span className="absolute inset-y-0 right-0 bg-crit-mark" style={{ left: pct(limits[2]) }} />
                <span className="absolute inset-y-0 bg-refused/60" style={{ left: pct(limits[1]), width: '2px' }} />
              </div>
              <div className="relative h-4">
                <span className="absolute -top-[1.05rem] h-4 w-[3px] -translate-x-1/2 rounded-sm bg-ink" style={{ left: pct(days) }} />
                {limits.map((n, i) => <span key={n} className="absolute top-0.5 -translate-x-1/2 text-[0.6875rem] text-ink-muted tabular-nums" style={{ left: pct(n) }}>L{i + 1} {n}</span>)}
              </div>
            </div>) : null}
          <p className="text-small text-ink-muted">
            {o.dueNowPaise > 0 ? `${inr(o.dueNowPaise)} due${o.pastDueDays > 0 ? `, ${o.pastDueDays} days late` : o.dueOn ? ` by ${shortDay(o.dueOn)}` : ''}` : 'Only this month so far; not yet due'}
            {` · last paid ${o.lastPaidOn ? shortDay(o.lastPaidOn) : 'never'}`}
          </p>
        </div>)}
    </Panel>
  );
}
